import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { SongSummary } from "../api/types";
import { navigate } from "../hooks/useLocation";

const RECENT_SONGS_PAGE_SIZE = 5;

/**
 * Calculates a clean pagination range to display, dynamically inserting
 * ellipses to prevent visual clutter and ensure responsive behaviour.
 */
function getPaginationRange(current: number, total: number): (number | string)[] {
  const range: (number | string)[] = [];
  const delta = 1;

  for (let i = 1; i <= total; i++) {
    if (
      i === 1 ||
      i === total ||
      (i >= current - delta && i <= current + delta)
    ) {
      range.push(i);
    } else if (
      (i === 2 && current > 3) ||
      (i === total - 1 && current < total - 2)
    ) {
      range.push("...");
    }
  }

  const result: (number | string)[] = [];
  for (let i = 0; i < range.length; i++) {
    if (range[i] === "..." && result[result.length - 1] === "...") {
      continue;
    }
    result.push(range[i]);
  }
  return result;
}

export interface RecentSongsProps {
  songs: SongSummary[];
}

export function RecentSongs({ songs }: RecentSongsProps) {
  const { t } = useTranslation();
  const searchInputId = useId();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const normalisedSearchTerm = searchTerm.trim().toLocaleLowerCase();
  const filteredSongs = useMemo(() => {
    if (normalisedSearchTerm.length === 0) return songs;
    return songs.filter((song) =>
      song.title.toLocaleLowerCase().includes(normalisedSearchTerm),
    );
  }, [normalisedSearchTerm, songs]);
  const totalPages = Math.max(
    1,
    Math.ceil(filteredSongs.length / RECENT_SONGS_PAGE_SIZE),
  );
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStartIndex = (safeCurrentPage - 1) * RECENT_SONGS_PAGE_SIZE;
  const currentPageSongs = filteredSongs.slice(
    pageStartIndex,
    pageStartIndex + RECENT_SONGS_PAGE_SIZE,
  );

  if (currentPage !== safeCurrentPage) {
    setCurrentPage(safeCurrentPage);
  }

  return (
    <section className="space-y-4">
      {songs.length > 0 && (
        <div className="w-full space-y-1">
          <label
            htmlFor={searchInputId}
            className="text-xs font-medium uppercase tracking-wide text-stone-400"
          >
            {t("recent.searchLabel")}
          </label>
          <input
            id={searchInputId}
            type="search"
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.currentTarget.value);
              setCurrentPage(1);
            }}
            placeholder={t("recent.searchPlaceholder")}
            className="w-full rounded-lg border border-stone-700 bg-stone-950 px-3 py-2 text-sm text-stone-100 outline-none transition-colors placeholder:text-stone-500 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/60"
          />
          <p className="text-xs text-stone-400" aria-live="polite">
            {t("recent.resultsStatus", { count: filteredSongs.length })}
          </p>
        </div>
      )}
      {songs.length === 0 ? (
        <p className="text-sm text-stone-400">{t("recent.empty")}</p>
      ) : filteredSongs.length === 0 ? (
        <p className="text-sm text-stone-400">{t("recent.emptySearch")}</p>
      ) : (
        <>
          <ul className="divide-y divide-stone-800 border-y border-stone-800">
            {currentPageSongs.map((song) => (
              <li key={song.job_id} className="py-4">
                <button
                  type="button"
                  onClick={() => navigate(`/songs/${song.job_id}`)}
                  className="block w-full min-w-0 truncate text-left text-sm font-medium text-stone-100 transition-colors hover:text-brand-300 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950"
                >
                  {song.title}
                </button>
              </li>
            ))}
          </ul>
          {totalPages > 1 && (
            <nav
              aria-label={t("recent.paginationLabel")}
              className="flex items-center justify-between gap-3"
            >
              <button
                type="button"
                disabled={safeCurrentPage === 1}
                onClick={() =>
                  setCurrentPage((page) => Math.max(1, page - 1))
                }
                className="rounded-md border border-stone-700 px-3 py-1.5 text-xs text-stone-200 transition-colors hover:bg-stone-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950 disabled:cursor-not-allowed disabled:border-stone-800 disabled:text-stone-500"
              >
                {t("recent.previousPage")}
              </button>
              <div className="flex items-center gap-1.5" aria-live="polite">
                {getPaginationRange(safeCurrentPage, totalPages).map((page, index) => {
                  if (page === "...") {
                    return (
                      <span
                        key={`ellipsis-${index}`}
                        className="w-8 h-8 flex items-center justify-center text-xs text-stone-500 select-none"
                      >
                        …
                      </span>
                    );
                  }

                  const isCurrent = page === safeCurrentPage;
                  return (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setCurrentPage(page as number)}
                      aria-current={isCurrent ? "page" : undefined}
                      aria-label={t("recent.goToPage", { page })}
                      className={`w-8 h-8 rounded-md flex items-center justify-center text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950 ${
                        isCurrent
                          ? "bg-brand-500 text-stone-950 font-bold hover:bg-brand-400"
                          : "border border-stone-700 text-stone-200 hover:bg-stone-800"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                disabled={safeCurrentPage === totalPages}
                onClick={() =>
                  setCurrentPage((page) => Math.min(totalPages, page + 1))
                }
                className="rounded-md border border-stone-700 px-3 py-1.5 text-xs text-stone-200 transition-colors hover:bg-stone-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950 disabled:cursor-not-allowed disabled:border-stone-800 disabled:text-stone-500"
              >
                {t("recent.nextPage")}
              </button>
            </nav>
          )}
        </>
      )}
    </section>
  );
}
